import { AlertTriangle, Inbox, RefreshCw } from 'lucide-react';
import * as React from 'react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

/**
 * Os quatro estados de tela (Playbook § Os quatro estados).
 *
 * Estão num arquivo só de propósito: o vocabulário visual precisa ser idêntico
 * em toda a aplicação, para que o usuário nunca precise adivinhar em qual dos
 * quatro ele está. Carregando e vazio NÃO podem parecer a mesma tela do erro.
 */

/** 1. Carregando — skeleton que reserva o espaço do conteúdo real. */
export function TabelaCarregando({ colunas, linhas = 6 }: { colunas: number; linhas?: number }) {
  return (
    <div className="flex flex-col gap-2 p-4" role="status" aria-label="Carregando dados">
      {Array.from({ length: linhas }).map((_, linha) => (
        <div key={linha} className="grid gap-3" style={{ gridTemplateColumns: `repeat(${colunas}, minmax(0,1fr))` }}>
          {Array.from({ length: colunas }).map((__, coluna) => (
            <Skeleton key={coluna} className="h-5" />
          ))}
        </div>
      ))}
    </div>
  );
}

/** 2. Vazio — ícone neutro + frase objetiva. Nunca é a tela de erro. */
export function EstadoVazio({
  titulo,
  descricao,
  acao,
  className,
}: {
  titulo: string;
  descricao: string;
  acao?: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col items-center gap-2 px-6 py-12 text-center', className)}>
      <Inbox className="size-6 text-icon" aria-hidden="true" />
      <p className="text-card-title">{titulo}</p>
      <p className="max-w-md text-body text-on-surface-muted">{descricao}</p>
      {acao ? <div className="mt-2">{acao}</div> : null}
    </div>
  );
}

/** 3. Erro — cita o que falhou e oferece "tentar de novo". */
export function EstadoErro({
  erro,
  aoTentarDeNovo,
  className,
}: {
  erro: Error | null;
  aoTentarDeNovo: () => void;
  className?: string;
}) {
  const detalhe = (erro as { detalhe?: string } | null)?.detalhe;
  return (
    <div role="alert" className={cn('flex flex-col items-center gap-2 px-6 py-12 text-center', className)}>
      <AlertTriangle className="size-6 text-danger" aria-hidden="true" />
      <p className="text-card-title text-danger">Não foi possível carregar</p>
      <p className="max-w-lg text-body text-on-surface">{erro?.message ?? 'A origem do problema não foi identificada.'}</p>
      {detalhe ? <p className="max-w-lg break-words text-label font-normal text-on-surface-muted">{detalhe}</p> : null}
      <Button variant="outline" className="mt-2" onClick={aoTentarDeNovo}>
        <RefreshCw aria-hidden="true" />
        Tentar de novo
      </Button>
    </div>
  );
}

/** 4. Sucesso parcial — o dado existe, mas a fonte ainda não está integrada. */
export function AguardandoFonte({ fonte, mapeado = false }: { fonte: string; mapeado?: boolean }) {
  return (
    <span
      className="pan-pending inline-flex items-center rounded-chip px-2 py-0.5 text-label"
      title={
        mapeado
          ? `Campo já identificado; falta integrar com ${fonte}.`
          : `A fonte deste indicador (${fonte}) ainda não foi localizada.`
      }
    >
      {mapeado ? `Mapeado · ${fonte}` : `Aguarda ${fonte}`}
    </span>
  );
}
